import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Plus, X, ScanSearch, Loader2, Image as ImageIcon, GitCompareArrows, FileText, ArrowRight, Info, Copy, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const QUICK = ["Cleaning", "Paint", "Carpet", "Damage", "Other"];

const STRENGTH = {
  strong: { label: "Strong match", cls: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]" },
  moderate: { label: "Moderate", cls: "bg-amber-soft text-amber-ink border-amber-border" },
  weak: { label: "Weak / none", cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]" },
};

export function DeductionAnalysis({ propertyId, onUseAnalysis }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([{ label: "", amount: "" }]);
  const [state, setState] = useState("idle"); // idle | analyzing
  const [analysis, setAnalysis] = useState(null);

  const setRow = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const addRow = (label = "") => setRows((r) => [...r, { label, amount: "" }]);
  const removeRow = (i) => setRows((r) => (r.length > 1 ? r.filter((_, idx) => idx !== i) : r));

  const valid = rows.filter((r) => r.label.trim() && parseFloat(r.amount) >= 0);

  const analyze = async () => {
    setState("analyzing");
    try {
      const { data } = await api.post(`/properties/${propertyId}/deduction-analysis`, {
        deductions: valid.map((r) => ({ label: r.label.trim(), amount: parseFloat(r.amount) || 0 })),
      });
      setAnalysis(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not analyze the deductions.";
      toast.error(msg);
    } finally {
      setState("idle");
    }
  };

  const openEvidence = (m) => {
    if (m.type === "photo" && m.image_path) window.open(fileUrl(m.image_path), "_blank");
    else if (m.type === "comparison_finding") navigate(`/compare/${propertyId}`);
    else navigate(`/property/${propertyId}`);
  };

  const total = analysis ? analysis.deductions.reduce((s, d) => s + (d.amount || 0), 0) : 0;
  const backedTotal = analysis
    ? analysis.deductions.filter((d) => d.strength === "strong" || d.strength === "moderate").reduce((s, d) => s + (d.amount || 0), 0)
    : 0;
  const anyMatched = analysis && analysis.deductions.some((d) => (d.matched_evidence || []).length > 0);

  const copyEvidence = async () => {
    const lines = analysis.deductions.map((d) => {
      const ev = (d.matched_evidence || []).map((m) => `    - [${m.type}] ${m.summary || ""}`).join("\n");
      return `• ${d.label} — $${(d.amount || 0).toLocaleString()} (${STRENGTH[d.strength]?.label || d.strength})\n`
        + `  ${d.rationale || ""}` + (ev ? `\n${ev}` : "\n    - No matching evidence");
    });
    const text = `Deposit deduction evidence summary\n$${backedTotal.toLocaleString()} of $${total.toLocaleString()} in charges are backed by strong or moderate evidence.\n\n${lines.join("\n\n")}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error("no clipboard api");
      }
      toast.success("Evidence summary copied to clipboard");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      let ok = false;
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      document.body.removeChild(ta);
      if (ok) toast.success("Evidence summary copied to clipboard");
      else toast.error("Couldn't copy — please try again.");
    }
  };

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="deduction-analysis">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><ScanSearch size={20} /></span>
        <div>
          <h3 className="font-heading font-semibold text-navy">What is your landlord charging for?</h3>
          <p className="text-sm text-[#475569]">Add each deduction. We'll match it to your documented evidence and rate how well it's supported.</p>
        </div>
      </div>

      {!analysis && (
        <>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="quick-add-chips">
            {QUICK.map((q) => (
              <button key={q} type="button" data-testid={`quick-chip-${q.toLowerCase()}`} onClick={() => addRow(q)}
                className="inline-flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] hover:border-brand hover:text-brand px-3 py-1 text-sm text-[#475569] transition-colors">
                <Plus size={13} /> {q}
              </button>
            ))}
          </div>

          <div className="mt-4 space-y-2">
            {rows.map((row, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`deduction-row-${i}`}>
                <Input data-testid={`deduction-label-${i}`} value={row.label} onChange={(e) => setRow(i, "label", e.target.value)}
                  placeholder="Charge label (e.g. Carpet cleaning)" className="rounded-xl flex-1" />
                <div className="relative w-28">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]">$</span>
                  <Input data-testid={`deduction-amount-${i}`} type="number" value={row.amount} onChange={(e) => setRow(i, "amount", e.target.value)}
                    placeholder="0" className="rounded-xl pl-6" />
                </div>
                <button type="button" data-testid={`deduction-remove-${i}`} onClick={() => removeRow(i)}
                  className="grid place-items-center h-9 w-9 rounded-lg text-[#94A3B8] hover:bg-[#FEE2E2] hover:text-[#B91C1C] shrink-0">
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-between">
            <button type="button" data-testid="add-deduction-row" onClick={() => addRow()}
              className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-hover">
              <Plus size={15} /> Add another charge
            </button>
            <Button data-testid="analyze-deductions-btn" onClick={analyze} disabled={valid.length === 0 || state === "analyzing"}
              className="rounded-xl bg-brand hover:bg-brand-hover text-white disabled:opacity-50">
              {state === "analyzing" ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing…</> : <>Match to my evidence</>}
            </Button>
          </div>
        </>
      )}

      {analysis && (
        <div className="mt-5" data-testid="deduction-results">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand/20 bg-brand/5 px-4 py-3" data-testid="disputable-total">
            <div className="flex items-start gap-2.5">
              <ShieldCheck size={20} className="text-brand mt-0.5 shrink-0" />
              <p className="text-sm text-[#334155]">
                <span className="font-heading text-base font-bold text-navy">${backedTotal.toLocaleString()}</span>
                <span className="text-[#475569]"> of ${total.toLocaleString()}</span> in charges {backedTotal > 0 ? "are" : "is"} backed by <span className="font-semibold text-navy">strong or moderate</span> evidence.
              </p>
            </div>
            <Button variant="outline" data-testid="copy-evidence-btn" onClick={copyEvidence}
              className="rounded-xl border-brand/30 text-brand hover:bg-brand/10 shrink-0">
              <Copy size={15} className="mr-2" /> Copy evidence list
            </Button>
          </div>
          {!anyMatched && (
            <div className="mb-4 rounded-xl border border-amber-border bg-amber-soft px-4 py-3 text-sm text-amber-ink" data-testid="deduction-empty-state">
              We couldn't match your documentation to these charges. That's okay — your letter will request itemized substantiation for each charge instead of overstating your evidence.
            </div>
          )}
          <div className="overflow-x-auto rounded-xl border border-[#E2E8F0]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8FAFC] text-left text-xs uppercase tracking-wide text-[#94A3B8]">
                  <th className="px-3 py-2 font-semibold">Deduction</th>
                  <th className="px-3 py-2 font-semibold">Amount</th>
                  <th className="px-3 py-2 font-semibold">Your evidence</th>
                  <th className="px-3 py-2 font-semibold">Strength</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {analysis.deductions.map((d, i) => {
                  const st = STRENGTH[d.strength] || STRENGTH.weak;
                  return (
                    <tr key={i} data-testid={`deduction-result-${i}`} className="align-top">
                      <td className="px-3 py-3 font-medium text-navy">{d.label}</td>
                      <td className="px-3 py-3 text-[#475569] whitespace-nowrap">${(d.amount || 0).toLocaleString()}</td>
                      <td className="px-3 py-3">
                        {d.matched_evidence?.length ? (
                          <div className="flex flex-wrap gap-1.5">
                            {d.matched_evidence.map((m, j) => (
                              <button key={j} type="button" data-testid={`evidence-link-${i}-${j}`} onClick={() => openEvidence(m)}
                                title={m.summary}
                                className="inline-flex items-center gap-1 rounded-full border border-[#CBD5E1] bg-white hover:border-brand hover:text-brand px-2 py-0.5 text-xs text-[#475569] max-w-[180px]">
                                {m.type === "photo" ? <ImageIcon size={11} /> : m.type === "comparison_finding" ? <GitCompareArrows size={11} /> : <FileText size={11} />}
                                <span className="truncate">{m.summary || m.type}</span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-[#94A3B8]">No matching evidence</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" data-testid={`strength-badge-${i}`}
                              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${st.cls}`}>
                              {st.label} <Info size={11} />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-64 text-sm text-[#334155]" data-testid={`strength-rationale-${i}`}>
                            {d.rationale || "No rationale provided."}
                          </PopoverContent>
                        </Popover>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <button type="button" data-testid="redo-analysis-btn" onClick={() => setAnalysis(null)}
              className="text-sm font-medium text-[#475569] hover:text-navy">Edit charges</button>
            <Button data-testid="use-analysis-btn" onClick={() => onUseAnalysis(analysis.analysis_id, total)}
              className="rounded-xl bg-brand hover:bg-brand-hover text-white">
              Generate dispute letter from this analysis <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
